
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export const ProtectedAdminRoute = ({ children }: ProtectedAdminRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { menuAccess, loading: permissionsLoading } = useAdminPermissions();
  const { isLoading, isAuthenticated, checkAttempted, adminRole } = useAdminAuth();
  const [permissionChecked, setPermissionChecked] = useState(false);

  useEffect(() => {
    // Check permissions based on the current path when auth is complete
    if (!isLoading && isAuthenticated && !permissionChecked) {
      setPermissionChecked(true);
      
      // If user doesn't have admin role, redirect to dashboard
      if (!adminRole) {
        console.log('User is not an admin, redirecting to dashboard');
        navigate("/dashboard");
        toast({
          title: "Access Denied",
          description: "You don't have permission to access admin pages.",
          variant: "destructive",
        });
        return;
      }
      
      const path = location.pathname;
      
      // Admin Users route check
      if (path.includes('/admin-users') && !menuAccess.adminUsers) {
        navigate("/admin/dashboard");
        toast({
          title: "Access Denied",
          description: "You don't have permission to access the Admin Users page.",
          variant: "destructive",
        });
        return;
      }
      
      // Settings route check
      if (path.includes('/settings') && !menuAccess.settings) {
        navigate("/admin/dashboard");
        toast({
          title: "Access Denied",
          description: "You don't have permission to access the Settings page.",
          variant: "destructive",
        });
        return;
      }
      
      // API routes check
      if ((path.includes('/pricing-api') || path.includes('/billing-webhook') || path.includes('/subscription-status-webhook')) 
          && !menuAccess.pricingApi && !menuAccess.paymentApi && !menuAccess.subscriptionStatusApi) {
        navigate("/admin/dashboard");
        toast({
          title: "Access Denied",
          description: "You don't have permission to access API pages.",
          variant: "destructive",
        });
        return;
      }
    }
  }, [
    isLoading, 
    isAuthenticated, 
    menuAccess, 
    location.pathname, 
    navigate, 
    toast, 
    permissionChecked,
    adminRole
  ]);

  // Reset permission check when location changes
  useEffect(() => {
    setPermissionChecked(false);
  }, [location.pathname]);

  // During initial load - show loader
  if (isLoading && !checkAttempted) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If authentication check was attempted but not authenticated - redirect to auth
  if (checkAttempted && !isAuthenticated) {
    return null; // Will be redirected by the useAdminAuth hook
  }

  // If authenticated but not admin, redirect to user dashboard
  if (checkAttempted && isAuthenticated && !adminRole) {
    return null; // Will be redirected by the useEffect above
  }

  // Always show the content if authenticated and admin
  return isAuthenticated && adminRole ? <>{children}</> : null;
};
